import { getPotentialOffcuts } from "../export/offcuts.js";
import { getOffcutMode } from "../export/strategies.js";
import { buildCutSimulationCaptionDescriptor } from "./workshopMessages.js";

/** @typedef {{ key: string, params?: Record<string, unknown> }} MessageDescriptor */
/** @typedef {{ literal: string }} LiteralMessage */

export const LEGACY_DEFAULT_CUT_INSTRUCTION = "Zagen op zaagmaat.";

const EDGE_SIDE_KEYS = {
  top: "core.edge.top",
  right: "core.edge.right",
  bottom: "core.edge.bottom",
  left: "core.edge.left",
};

/**
 * @param {object} sourcePanel
 * @returns {MessageDescriptor}
 */
export function buildGrainDescriptionDescriptor(sourcePanel) {
  if (sourcePanel.grain) {
    return { key: "core.grain.fixed" };
  }
  if (sourcePanel.allowRotation) {
    return { key: "core.grain.free" };
  }
  return { key: "core.grain.noRotation" };
}

/**
 * @param {string} strategyId
 * @returns {MessageDescriptor}
 */
export function buildCutStrategyLabelDescriptor(strategyId) {
  return { key: `core.strategy.${strategyId}.label` };
}

/**
 * @param {string} strategyId
 * @returns {MessageDescriptor}
 */
export function buildCutStrategyDescriptionDescriptor(strategyId) {
  return { key: `core.strategy.${strategyId}.description` };
}

/**
 * @param {"whenToUse"|"whenBlocked"|"noEligibleSheets"} section
 * @param {"title"|"body"|"example"|"fallback"} field
 * @returns {MessageDescriptor}
 */
export function buildStripHelpDescriptor(section, field) {
  return { key: `core.strip.${section}.${field}` };
}

/**
 * @param {"horizontal"|"vertical"} axisKey
 * @param {number} position
 * @param {string|null} panelName
 * @returns {MessageDescriptor}
 */
export function buildStripCrossesPanelDescriptor(axisKey, position, panelName) {
  return {
    key: "core.strip.crossesPanel",
    params: {
      axisKey,
      position: Math.round(position),
      panelName: panelName || null,
    },
  };
}

/**
 * @param {object} offcut
 * @returns {MessageDescriptor}
 */
export function buildOffcutCellLabelDescriptor(offcut) {
  if (offcut.label === "volledige plaat") {
    return { key: "core.offcut.fullSheet" };
  }
  if (offcut.label === "los zaagrest") {
    return { key: "core.offcut.loose" };
  }
  if (offcut.label === "rechter restvlak") {
    return { key: "core.offcut.rightPanel" };
  }
  if (offcut.label === "onderste restvlak") {
    return { key: "core.offcut.bottomPanel" };
  }
  return { key: "core.offcut.freeArea" };
}

/**
 * @param {string} strategy
 * @param {boolean} stripsNoCrosscut
 * @returns {MessageDescriptor}
 */
export function buildOffcutLabelDescriptor(strategy, stripsNoCrosscut) {
  if (strategy === "strips" && stripsNoCrosscut) {
    return { key: "core.boardLabel.offcutSelfTrim" };
  }
  return { key: "core.boardLabel.offcutDefault" };
}

/**
 * @param {string} strategy
 * @param {boolean} stripsNoCrosscut
 * @returns {MessageDescriptor|null}
 */
export function buildStripModeBannerDescriptor(strategy, stripsNoCrosscut) {
  if (strategy !== "strips") {
    return null;
  }

  return stripsNoCrosscut
    ? { key: "core.boardLabel.stripBannerNoCrosscut" }
    : { key: "core.boardLabel.stripBannerCrosscut" };
}

/**
 * @param {string} strategy
 * @param {boolean} stripsNoCrosscut
 * @param {string} placementName
 * @returns {MessageDescriptor|string}
 */
export function buildPlacementCaptionDescriptor(strategy, stripsNoCrosscut, placementName) {
  if (stripsNoCrosscut && strategy === "strips") {
    return { key: "core.boardLabel.placementStripSelfTrim" };
  }
  return placementName;
}

/**
 * @param {object[]} cuts
 * @param {string} strategy
 * @returns {MessageDescriptor|null}
 */
export function buildCutSequenceSummaryDescriptor(cuts, strategy) {
  const count = cuts.length;
  if (count === 0) {
    return null;
  }

  const modeKey = strategy === "strips"
    ? "strips"
    : strategy === "preserve_offcuts"
      ? "preserve"
      : "efficient";

  return {
    key: count === 1 ? "core.cutSummary.singular" : "core.cutSummary.plural",
    params: { count, modeKey },
  };
}

/**
 * @param {object|null} cut
 * @param {number} boardLength
 * @param {number} boardWidth
 * @returns {{ title: MessageDescriptor, detail: MessageDescriptor|null }}
 */
export function buildCutPreviewInstructionDescriptor(cut, boardLength, boardWidth) {
  if (!cut) {
    return {
      title: { key: "core.preview.noCutsTitle" },
      detail: { key: "core.preview.noCutsDetail" },
    };
  }

  return {
    title: {
      key: "core.preview.cutTitle",
      params: {
        number: cut.number,
        caption: buildCutSimulationCaptionDescriptor(cut, boardLength, boardWidth),
      },
    },
    detail: null,
  };
}

/**
 * @param {string} strategy
 * @param {object} sheet
 * @param {object} sourcePanel
 * @param {object} exportDraft
 * @returns {MessageDescriptor}
 */
export function buildSheetStrategyInstructionDescriptor(
  strategy,
  sheet,
  sourcePanel,
  exportDraft,
) {
  const offcutMode = getOffcutMode(strategy, exportDraft);
  const offcuts = getPotentialOffcuts(
    sheet,
    sourcePanel,
    Number(exportDraft.minOffcutLength || 500),
    Number(exportDraft.minOffcutWidth || 300),
    offcutMode,
  );
  const emphasizeExact = Boolean(exportDraft.emphasizeExactSizes);

  if (strategy === "efficient_cutting") {
    return {
      key: "core.sheetInstruction.efficient",
      params: { emphasizeExact },
    };
  }

  if (strategy === "preserve_offcuts") {
    if (offcuts.length) {
      return {
        key: "core.sheetInstruction.preserveWithOffcut",
        params: {
          emphasizeExact,
          offcutLength: offcuts[0].length,
          offcutWidth: offcuts[0].width,
          offcutLabel: buildOffcutCellLabelDescriptor(offcuts[0]),
        },
      };
    }
    return {
      key: "core.sheetInstruction.preserve",
      params: { emphasizeExact },
    };
  }

  if (strategy === "strips") {
    if (offcuts.length) {
      return {
        key: "core.sheetInstruction.stripsWithOffcuts",
        params: {
          emphasizeExact,
          offcuts: offcuts.map((offcut) => ({
            length: offcut.length,
            width: offcut.width,
          })),
        },
      };
    }
    return {
      key: "core.sheetInstruction.strips",
      params: { emphasizeExact },
    };
  }

  return { key: "core.sheetInstruction.empty" };
}

function hasDifferentOrderSize(row) {
  return Number(row.orderLength) !== Number(row.finalLength)
    || Number(row.orderWidth) !== Number(row.finalWidth);
}

/**
 * @param {object} row
 * @param {object} exportDraft
 * @returns {MessageDescriptor|LiteralMessage}
 */
export function buildDefaultRowInstructionDescriptor(row, exportDraft) {
  if (row.instruction && row.instruction !== LEGACY_DEFAULT_CUT_INSTRUCTION) {
    return { literal: row.instruction };
  }

  if (exportDraft.globalStrategy === "strips") {
    return { key: "core.rowInstruction.stripDeliver" };
  }

  if (hasDifferentOrderSize(row)) {
    const actions = [];
    if (Number(row.orderLength) > Number(row.finalLength)) {
      actions.push({
        key: "core.rowInstruction.trimLength",
        params: {
          from: Number(row.orderLength),
          to: Number(row.finalLength),
        },
      });
    }
    if (Number(row.orderWidth) > Number(row.finalWidth)) {
      actions.push({
        key: "core.rowInstruction.trimWidth",
        params: {
          from: Number(row.orderWidth),
          to: Number(row.finalWidth),
        },
      });
    }
    if (actions.length) {
      return { key: "core.rowInstruction.joined", params: { parts: actions } };
    }
    return { key: "core.rowInstruction.orderDiffers" };
  }

  if (exportDraft.globalStrategy === "preserve_offcuts") {
    return {
      key: exportDraft.emphasizeExactSizes
        ? "core.rowInstruction.exactPreserve"
        : "core.rowInstruction.cutPreserve",
    };
  }

  return {
    key: exportDraft.emphasizeExactSizes
      ? "core.rowInstruction.exact"
      : "core.rowInstruction.cutPerSize",
  };
}

/**
 * @param {object} row
 * @param {object} exportDraft
 * @param {object} [exportRow={}]
 * @returns {MessageDescriptor|LiteralMessage|string}
 */
export function buildShortRowInstructionDescriptor(row, exportDraft, exportRow = {}) {
  const instructionDescriptor = exportRow.instructionEdited
    ? (exportRow.instruction ? { literal: exportRow.instruction } : { literal: "" })
    : buildDefaultRowInstructionDescriptor(row, exportDraft);

  if ("literal" in instructionDescriptor && !instructionDescriptor.literal.trim()) {
    return { literal: "" };
  }

  if ("literal" in instructionDescriptor) {
    const text = instructionDescriptor.literal.toLowerCase();
    if (text.includes("zelf inkorten") || text.includes("trim")) {
      return { key: "core.rowInstruction.shortOrderSize" };
    }
  }

  if (exportDraft.globalStrategy === "strips") {
    return { key: "core.rowInstruction.shortStrip" };
  }
  if (exportDraft.globalStrategy === "preserve_offcuts") {
    return { key: "core.rowInstruction.shortExact" };
  }
  if ("literal" in instructionDescriptor) {
    return { key: "core.rowInstruction.shortOrderSize" };
  }

  return exportDraft.emphasizeExactSizes
    ? { key: "core.rowInstruction.shortExact" }
    : { key: "core.rowInstruction.shortCut" };
}

/**
 * @param {object} panel
 * @returns {MessageDescriptor|LiteralMessage}
 */
export function buildCutInstructionDescriptor(panel) {
  if (panel.cutNote) {
    return { literal: panel.cutNote };
  }

  const { orderLength, orderWidth, finalLength, finalWidth } = {
    orderLength: panel.orderLength != null && panel.orderLength !== ""
      ? Number(panel.orderLength)
      : Number(panel.length || 0),
    orderWidth: panel.orderWidth != null && panel.orderWidth !== ""
      ? Number(panel.orderWidth)
      : Number(panel.width || 0),
    finalLength: Number(panel.length || 0),
    finalWidth: Number(panel.width || 0),
  };

  if (orderLength === finalLength && orderWidth === finalWidth) {
    return { key: "core.rowInstruction.cutToSize" };
  }

  const actions = [];
  if (orderLength > finalLength) {
    actions.push({
      key: "core.rowInstruction.trimLength",
      params: { from: orderLength, to: finalLength },
    });
  }
  if (orderWidth > finalWidth) {
    actions.push({
      key: "core.rowInstruction.trimWidth",
      params: { from: orderWidth, to: finalWidth },
    });
  }
  if (!actions.length) {
    return { key: "core.rowInstruction.orderDiffers" };
  }
  return { key: "core.rowInstruction.joined", params: { parts: actions } };
}

/**
 * @param {Record<string, boolean>} edges
 * @returns {MessageDescriptor}
 */
export function buildEdgeTextDescriptor(edges = {}) {
  const sides = Object.entries(edges)
    .filter(([, value]) => Boolean(value))
    .map(([key]) => EDGE_SIDE_KEYS[key])
    .filter(Boolean);

  if (!sides.length) {
    return { key: "core.edge.none" };
  }

  return {
    key: sides.length === 1 ? "core.edge.single" : "core.edge.list",
    params: { sides },
  };
}

/** @returns {MessageDescriptor[]} */
export function buildCutListCsvHeaderDescriptors() {
  return [
    { key: "core.csv.quantity" },
    { key: "core.csv.orderLength" },
    { key: "core.csv.orderWidth" },
    { key: "core.csv.finalLength" },
    { key: "core.csv.finalWidth" },
    { key: "core.csv.thickness" },
    { key: "core.csv.description" },
    { key: "core.csv.project" },
    { key: "core.csv.material" },
    { key: "core.csv.edges" },
    { key: "core.csv.sheet" },
    { key: "core.csv.instruction" },
  ];
}
